import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content for safe display while preserving formatting
 * Use this when you want to show formatted email content with styles
 *
 * @param html - Raw HTML content from email
 * @returns Sanitized HTML safe for rendering with dangerouslySetInnerHTML
 */
export function sanitizeHtmlForDisplay(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_ATTR: [
      'href',
      'title',
      'class',
      // Table attributes
      'colspan',
      'rowspan',
      'align',
      'valign',
    ],
    ALLOWED_TAGS: [
      // Text formatting
      'p',
      'br',
      'strong',
      'em',
      'b',
      'i',
      'u',
      's',
      'mark',
      // Lists
      'ul',
      'ol',
      'li',
      // Links
      'a',
      // Containers
      'div',
      'span',
      'blockquote',
      // Tables (common in emails)
      'table',
      'thead',
      'tbody',
      'tr',
      'td',
      'th',
    ],
    FORBID_ATTR: ['onerror', 'onload', 'onclick'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
  });
}

/**
 * Strips HTML tags and decodes HTML entities to get clean text for diffing
 * Preserves line breaks by converting <br> and block-level tags to newlines
 */
export const stripHtml = (html: string): string => {
  const tempDiv = document.createElement('div');

  // Replace <br> and block-level closing tags with newlines before stripping
  const htmlWithNewlines = html
    .replaceAll(/<br\s*\/?>/gi, '\n')
    .replaceAll('</p>', '\n')
    .replaceAll('</div>', '\n')
    .replaceAll('</li>', '\n')
    .replaceAll(/<\/h[1-6]>/gi, '\n');

  tempDiv.innerHTML = htmlWithNewlines;
  return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * Detects if text contains HTML tags
 */
export const isHtml = (text: string): boolean => {
  return /<[^>]+>/.test(text);
};

export interface EmailDraft {
  bcc?: string;
  body: string;
  cc?: string;
  subject: string;
  to: string;
}

const stripEmptyHeaderLines = (text: string): string =>
  text
    .split('\n')
    .filter((line) => !/^(To|From|Cc|Bcc|Body):\s*$/i.test(line))
    .join('\n')
    .trim();

export const parseEmailDraft = (content: string): EmailDraft | null => {
  // Strip tags if they exist (safety measure)
  const cleanContent = content
    .replaceAll(/<<<EMAIL_DRAFT>>>|<<<END_EMAIL_DRAFT>>>/g, '')
    .replaceAll(
      /<<<REVISION>>>|<<<REVISED>>>|<<<END_REVISED>>>|<<<END_REVISION>>>|<<<ORIGINAL>>>[\s\S]*?<<<END_ORIGINAL>>>/g,
      '',
    )
    .trim();

  const subjectRegex = /Subject:[^\S\n]*(.+?)(?=\n|$)/i;
  const toRegex = /To:[^\S\n]*(.+?)(?=\n|$)/i;
  const ccRegex = /Cc:[^\S\n]*(.+?)(?=\n|$)/i;
  const bccRegex = /Bcc:[^\S\n]*(.+?)(?=\n|$)/i;
  const bodyRegex = /Body:\s*([\s\S]+)/i;

  const subjectMatch = subjectRegex.exec(cleanContent);
  const toMatch = toRegex.exec(cleanContent);
  const ccMatch = ccRegex.exec(cleanContent);
  const bccMatch = bccRegex.exec(cleanContent);
  const bodyMatch = bodyRegex.exec(cleanContent);

  let body = bodyMatch?.[1]?.trim() ?? '';

  if (!body) {
    // If no explicit "Body:" tag, try to find where the headers end and take the rest
    const matches = [subjectMatch, toMatch, ccMatch, bccMatch].filter(
      (m): m is RegExpExecArray => !!m,
    );

    if (matches.length > 0) {
      // Find the end index of the last header
      const lastHeaderEnd = Math.max(...matches.map((m) => m.index + m[0].length));
      // Take everything after the last header
      body = cleanContent.slice(lastHeaderEnd).trim();
    } else {
      // If no headers found at all, and no Body tag, assume the whole content is the body
      // if it doesn't look like it's just tags or empty
      if (cleanContent.length > 0) {
        body = cleanContent;
      }
    }
  }

  return {
    subject: subjectMatch?.[1]?.trim() ?? '',
    to: toMatch?.[1]?.trim() ?? '',
    ...(ccMatch?.[1]?.trim() && { cc: ccMatch[1].trim() }),
    ...(bccMatch?.[1]?.trim() && { bcc: bccMatch[1].trim() }),
    body: stripEmptyHeaderLines(body),
  };
};
